import React, { useContext, useEffect, useMemo } from 'react';
import {
  IClientBusinessModel,
  IClientAssociationModel,
} from '../../../../../../Types/ClientModelTypes';
import { findCollectionStaticSafe } from '../../../../../../Model/avenyStaticModelUtils';
import { MdModalRoute } from '../../../../../../Components/MasterDetail/Components/MdModal/MdModalRoute';
import { nth, path } from 'ramda';
import EDIconButton from '../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import { createPortal } from 'react-dom';
import BusinessDataObjectDetailHeader from '../BusinessDataObjectDetailHeader/BusinessDataObjectDetailHeader';
import { IMasterCompProps } from '../../../../../../Types/MasterDetailTypes';
import { BreadcrumbsContext } from '../../RutinBreadcrumbs/BreadcrumbsContext';
import {
  BusinessDataFormContext,
  NewFormContext,
} from '../BusinessDataForm/BusinessDataForm';
import { attributeDefToDataPath } from '../../../../../../Utils/dataUtils';
import { IObjektVy } from '../../../../../../Types/ObjektVy';
import businessCollectionComponents, {
  defaultStandardDef,
} from '../businessCollectionComponents';
import MultiVy from '../../../../../../Components/MultiVy/MultiVy';
import { InternalError } from '../../../../../../Components/InternalError/InternalError';
import EDLink from '../../../../../../BaseComponents/Link/EDLink';
import GroupHeader from '../GroupHeader/GroupHeader';

interface IProps {
  association: IClientAssociationModel;
  vy: React.ReactElement<any>;
}

const AssocieradObjektVy: React.FC<React.PropsWithChildren<IProps>> = props => {
  const { association, vy } = props;
  const formContext = useContext(BusinessDataFormContext);
  const pathString = attributeDefToDataPath(
    `${association.AssociationName}.id`
  );

  const associatedId = formContext?.data
    ? (path(pathString, formContext?.data) as string)
    : null;

  const assocBmod: IClientBusinessModel = findCollectionStaticSafe(
    association.AssociatedCollectionName,
    'felaktig association'
  );

  const master = useMemo(() => {
    return {
      caption: formContext?.model.RootType.SBLabel ?? '',
      comp: (masterprops: IMasterCompProps) => {
        const {
          headerReferences,
          currentNavLevel,
          topNavLevel,
          backButtonReferences,
          registerActiveNavLevel,
          unregisterActiveNavLevel,
          // eslint-disable-next-line react-hooks/rules-of-hooks
        } = useContext(BreadcrumbsContext);
        const headerRef =
          masterprops.selectedDetail && currentNavLevel !== undefined
            ? headerReferences[currentNavLevel]
            : undefined;
        const renderHeader = headerRef != null;

        // eslint-disable-next-line react-hooks/rules-of-hooks
        useEffect(() => {
          if (renderHeader && currentNavLevel) {
            registerActiveNavLevel(currentNavLevel);
            return () => unregisterActiveNavLevel(currentNavLevel);
          }
          return undefined;
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [renderHeader, currentNavLevel]);

        const backRef =
          masterprops.selectedDetail &&
          currentNavLevel !== undefined &&
          currentNavLevel === topNavLevel
            ? nth(currentNavLevel, backButtonReferences) || null
            : null;
        const unselectDetail = () => masterprops.selectDetail(null);
        const selectCurrentDetail = () =>
          masterprops.selectDetail(masterprops.selectedDetail);

        return (
          <>
            <div
              style={{
                display: 'flex',
              }}
            >
              {!formContext?.formState.editMode && (
                <EDLink
                  onClick={() => masterprops.selectDetail(associatedId)}
                  variant="overline"
                  buttonComponent
                >
                  {association.DisplayLabel}
                </EDLink>
              )}
              {formContext?.formState.editMode && (
                <GroupHeader>{association.DisplayLabel}</GroupHeader>
              )}
            </div>
            {vy}
            <BusinessDataObjectDetailHeader
              collectionName={association.AssociatedCollectionName}
              headerRef={headerRef}
              id={associatedId}
              onClick={
                currentNavLevel !== topNavLevel
                  ? selectCurrentDetail
                  : undefined
              }
            />
            {backRef &&
              createPortal(
                <EDIconButton
                  icon="arrow_back"
                  color="inherit"
                  tooltip={`Tillbaka till ${formContext?.model.RootType.SBLabel}`}
                  padding="dense"
                  marginTop={0.5}
                  marginBottom={0.5}
                  onClick={unselectDetail}
                />,
                backRef
              )}
          </>
        );
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [associatedId, formContext?.formState.editMode]);

  const detail = useMemo(() => {
    return {
      caption: association.DisplayLabel,
      comp: () => {
        const ObjektVy =
          businessCollectionComponents(assocBmod.CollectionName).find(
            (comp: IObjektVy) => comp.id === 'standard'
          ) || defaultStandardDef;
        const Comp = ObjektVy.def(assocBmod).comp;
        return (
          <NewFormContext>
            <MultiVy
              tabbed
              vyProps={{ id: associatedId }}
              vyer={[
                {
                  caption: association.DisplayLabel,
                  comp: Comp,
                  collectionName: association.AssociatedCollectionName,
                },
              ]}
            />
          </NewFormContext>
        );
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [association.DisplayLabel, assocBmod, associatedId]);

  if (!formContext) {
    return (
      <InternalError
        id={'141a8b0c-1fbf-4ddd-9ba8-b7cd913a9dd5'}
        message="AssocieradObjektVy kan inte visas utanför BusinessDataFormContext"
      />
    );
  }

  return (
    <MdModalRoute
      detailRouteName={association.AssociationName}
      master={master}
      detail={detail}
      collection={assocBmod}
    />
  );
};

export default AssocieradObjektVy;
